import { useMemo } from 'react';
import * as Y from 'yjs';
import { swapOrder, type OrderedEntry } from '@/lib/sync/surface-order';
import { SURFACES_ROOT, readSurfaces } from '@/lib/sync/document-surfaces';
import { createSurface } from '@/lib/sync/create-surface';
import { renameSurface } from '@/lib/sync/rename-surface';

export interface SurfaceMutations {
  /** The new id, or `null` if refused (at the cap, `keyAfter` returned `null`,
   *  or `explicitId` already names an existing entry). `explicitId` lets a
   *  caller mint an id up front (e.g. the diagram surface's independent
   *  `crypto.randomUUID()`) instead of a random one generated here —
   *  `fragment` is always derived from the id actually used, so the two
   *  never diverge. */
  create: (
    title: string,
    surface?: 'text' | 'canvas' | 'audio',
    generator?: string | null,
    explicitId?: string,
  ) => string | null;
  rename: (id: string, title: string) => void;
  move: (id: string, direction: 'up' | 'down') => void;
  remove: (id: string) => void;
  bindRecording: (surfaceId: string, recordingId: string) => void;
}


function findMapIndexById(root: Y.Array<Y.Map<unknown>>, id: string): number {
  for (let i = 0; i < root.length; i += 1) {
    if (root.get(i).get('id') === id) return i;
  }
  return -1;
}

/**
 * The four surface manifest mutations, each a single Yjs transaction. `doc`
 * may be `null` while the entity's Yjs doc hasn't loaded yet — every mutation
 * is then a no-op.
 */
export function useSurfaceMutations(doc: Y.Doc | null): SurfaceMutations {
  return useMemo(() => ({
    create: (
      title: string,
      surface: 'text' | 'canvas' | 'audio' = 'text',
      generator: string | null = null,
      explicitId?: string,
    ): string | null => createSurface(doc, { title, surface, generator, id: explicitId }),

    rename: (id: string, title: string): void => renameSurface(doc, id, title),

    move: (id: string, direction: 'up' | 'down'): void => {
      if (!doc) return;
      const entries = readSurfaces(doc);
      const ordered: OrderedEntry[] = entries.map((e) => ({ id: e.id, order: e.order }));
      const swap = swapOrder(ordered, id, direction);
      if (swap === null) return;

      const root = doc.getArray<Y.Map<unknown>>(SURFACES_ROOT);
      doc.transact(() => {
        const movedIndex = findMapIndexById(root, swap.moved.id);
        const displacedIndex = findMapIndexById(root, swap.displaced.id);
        if (movedIndex === -1 || displacedIndex === -1) return;
        root.get(movedIndex).set('order', swap.moved.order);
        root.get(displacedIndex).set('order', swap.displaced.order);
      });
    },

    remove: (id: string): void => {
      if (!doc) return;
      const root = doc.getArray<Y.Map<unknown>>(SURFACES_ROOT);
      const index = findMapIndexById(root, id);
      if (index === -1) return;

      const entry = root.get(index);
      const fragment = entry.get('fragment');
      const surface = entry.get('surface');

      doc.transact(() => {
        if (typeof fragment === 'string') {
          if (surface === 'canvas') {
            // A canvas's roots (nodes/edges/zOrder, …) live as generic Y.Map
            // children of the top-level Y.Map at this name — cleared without
            // any canvas-specific knowledge, mirroring how the text path
            // clears an XmlFragment's children without knowing Tiptap's
            // schema. Yjs cannot remove a root type, so the empty map root
            // remains, same as a cleared XmlFragment.
            doc.getMap(fragment).clear();
          } else {
            const frag = doc.getXmlFragment(fragment);
            frag.delete(0, frag.length);
          }
        }
        root.delete(index, 1);
      });
    },

    bindRecording: (surfaceId: string, recordingId: string): void => {
      if (!doc) return;
      const root = doc.getArray<Y.Map<unknown>>(SURFACES_ROOT);
      const index = findMapIndexById(root, surfaceId);
      if (index === -1) return;

      doc.transact(() => {
        root.get(index).set('recordingId', recordingId);
      });
    },
  }), [doc]);
}
